import React, { useState } from 'react';
import { ENDPOINTS } from 'features/command-center/constants';
import { useShowAlert } from 'hooks';
import useDatatable, {
  buildRowActionsRendererDefinition,
  getColumnPreferences,
  getColumnsToRender
} from 'hooks/datatable';
import { rowClickBuilder } from 'hooks/profileActions';
import { SECONDARY_API_CLIENT } from 'services/API';
import { Permissions, userHasPermission } from 'services/Authorization';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import FPActionButton from 'UI/components/atoms/FPActionButton';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import {
  ScrollableDataTable,
  ScrollableDataTableLayout
} from 'UI/components/templates/ScrollableDataTableLayout';
import { RowActions } from 'UI/constants/defaults';
import { componentDimensions } from 'UI/constants/dimensions';
import { SvgAdd, SvgNoNotes } from 'UI/res';

import { FeedbackNoteDetails } from '../FeedbackNoteDetails';

import { COLUMNS, ORDER_BY_OPTIONS, PAGE_KEY } from './FeedbackNotesList.constants';
import { ButtonContainer, TopContentWrapper } from './FeedbackNotesList.styled';
import { formatResponse } from './FeedbackNotesList.utils';

const ICON_BUTTON_SIZE = 16;
const COPIES = strings.commandCenter.feedbackNotes;
const { emptyState } = componentDimensions;

export const FeedbackNotesList = ({ employeeNumber, onNewItemClick }) => {
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { showAlert } = useShowAlert();
  const canDelete = userHasPermission(Permissions.CommandCenter.FeedbackLogs.AllowDeletion);

  const initialPreferences = getColumnPreferences(PAGE_KEY, 0, ORDER_BY_OPTIONS, COLUMNS);
  const {
    columnPreferences,
    count,
    data,
    getData,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleColumnSortChange,
    handleFiltersToggle,
    handleKeywordChange,
    handlePageChange,
    handlePerPageChange,
    listState
  } = useDatatable({
    apiInstance: SECONDARY_API_CLIENT,
    columnsDefinitions: COLUMNS,
    endpoint: ENDPOINTS.feedbackNotesByEmployee.replace(':employeeNumber', employeeNumber),
    formatResponse,
    initialPreferences,
    key: PAGE_KEY,
    orderByOptions: ORDER_BY_OPTIONS,
    periodDefaultValue: null,
    sendDateWithTimezone: true,
    shouldScrollOnNavigate: true
  });

  const { isLoading, showWholeSkeleton, page, perPage, keyword } = listState;
  const { columns, columnOrder, orderBy, direction } = columnPreferences;

  const actions = [
    {
      name: RowActions.View,
      onClick: rowClickBuilder({
        columns,
        data,
        pickId: true,
        callback: id => setSelectedNoteId(id)
      })
    }
  ];

  const actionsColumn = buildRowActionsRendererDefinition({
    actions,
    columns
  });

  const finalColumns = getColumnsToRender([...columns, actionsColumn]);

  const handleCloseDrawer = () => {
    setSelectedNoteId(null);
  };

  const buttonDefinitions = [
    {
      props: {
        onClick: handleCloseDrawer,
        text: strings.shared.ui.close,
        variant: 'outlined'
      },
      GridItemProps: {
        md: 'auto'
      }
    },
    {
      props: {
        onClick: () => setIsConfirmationDialogOpen(true),
        text: COPIES.drawer.deleteNote.title
      },
      GridItemProps: {
        md: 'auto'
      },
      visible: canDelete
    }
  ];

  const handleDeleteNote = async () => {
    try {
      if (!selectedNoteId) return;

      setIsDeleting(true);
      await SECONDARY_API_CLIENT.delete(ENDPOINTS.feedbackNotesById.replace(':id', selectedNoteId));

      showAlert({
        title: strings.formatString(COPIES.drawer.deleteNote.alerts.success, { action: 'deleted' }),
        autoHideDuration: 5000
      });

      setIsDeleting(false);
      setIsConfirmationDialogOpen(false);
      setSelectedNoteId(null);
      handleCloseDrawer();
      await getData();
    } catch (error) {
      setIsDeleting(false);

      showAlert({
        title: strings.formatString(COPIES.drawer.deleteNote.alerts.error, { action: 'deleting' }),
        error
      });
    }
  };

  const deleteNoteActions = [
    {
      variant: 'outlined',
      text: strings.shared.ui.cancel,
      onClick: () => setIsConfirmationDialogOpen(false),
      disabled: isDeleting
    },
    {
      text: COPIES.drawer.deleteNote.title,
      onClick: handleDeleteNote,
      disabled: isDeleting
    }
  ];

  return (
    <ScrollableDataTableLayout
      topContent={
        <TopContentWrapper container item xs={12} justifyContent="flex-end">
          <ButtonContainer item>
            <FPActionButton
              icon={SvgAdd}
              text={COPIES.drawer.form.mainAction}
              onClick={() => onNewItemClick && onNewItemClick()}
              iconProps={{ size: ICON_BUTTON_SIZE }}
            />
          </ButtonContainer>
        </TopContentWrapper>
      }
      content={
        <>
          <ScrollableDataTable
            columnOrder={columnOrder?.length ? columnOrder : undefined}
            columns={finalColumns}
            count={count ?? 0}
            customEmptyState={<SvgNoNotes size={emptyState.width} />}
            data={data}
            draggableColumns={{
              enabled: true
            }}
            elevation={0}
            filter={false}
            loading={showWholeSkeleton}
            onColumnDisplayClick={handleColumnDisplayChange}
            onColumnOrderChange={handleColumnOrderChange}
            onColumnSortChange={handleColumnSortChange}
            onPageClick={handlePageChange}
            onPerPageClick={handlePerPageChange}
            onSearchTextChange={handleKeywordChange}
            onToggleFilters={handleFiltersToggle}
            page={page}
            refreshing={isLoading}
            rowsPerPage={perPage}
            searchText={keyword}
            selectableRows="none"
            selectToolbarPlacement="none"
            sortOrder={{ name: orderBy, direction }}
            subtitle={COPIES.emptyState.notes.subtitle}
            title={COPIES.emptyState.notes.title}
          />
          {!!selectedNoteId && (
            <FPDrawer
              CardHeaderProps={{
                title: COPIES.noteDetailsDrawer.title
              }}
              FPDrawerProps={{
                open: true,
                onClose: handleCloseDrawer
              }}
              onClose={handleCloseDrawer}
              hasPaddingContent={false}
              footer={
                <FPButtonBuilder
                  GridContainerProps={{ spacing: 2 }}
                  buttonDefinitions={buttonDefinitions}
                />
              }
            >
              <FeedbackNoteDetails id={selectedNoteId} />

              <When condition={isConfirmationDialogOpen && canDelete}>
                <DecisionDialog
                  actions={deleteNoteActions}
                  isOpened
                  message={COPIES.drawer.deleteNote.confirmationDialog.message}
                  onClose={() => setIsConfirmationDialogOpen(false)}
                  showSeverityIcon
                  title={COPIES.drawer.deleteNote.confirmationDialog.title}
                />
              </When>
            </FPDrawer>
          )}
        </>
      }
    />
  );
};
